var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import mergeWith from 'lodash.mergewith';
import { HawkinsJSSProvider } from '@hawkins/styles';
import { classesMergeFn, HawkinsContext, propsMergeFn, useHawkinsContext } from '../hooks';
import { CSSBaseline } from './cssBaseline';
import { CSSReset } from './cssReset';
/**
 * A wrapper context that allows for full control over the `theme`, `defaultClasses` and `defaultProps`.
 *
 * There are two top level props (`compact` and `loading`) that can be set to quickly set all components
 * within this context to have those props.
 *
 * The [useHawkinsContext](components-use-hawkins-context) hook can be used to access the current Hawkins context to help
 * toggle the current theme.
 *
 * The [useTheme](/styles-use-theme) hook can be used to access the current theme variables.
 */
export var HawkinsProvider = function (_a) {
    var children = _a.children, compact = _a.compact, defaultClasses = _a.defaultClasses, defaultProps = _a.defaultProps, externalTheme = _a.externalTheme, generateClassName = _a.generateClassName, _b = _a.includeCssBaseline, includeCssBaseline = _b === void 0 ? true : _b, _c = _a.includeCssReset, includeCssReset = _c === void 0 ? true : _c, insertionPoint = _a.insertionPoint, loading = _a.loading, themeProp = _a.theme, hideDeprecationWarnings = _a.hideDeprecationWarnings;
    // State
    var _d = __read(React.useState(themeProp), 2), internalTheme = _d[0], setInternalTheme = _d[1];
    // Keep the internal theme state in sync with `props.theme`.
    React.useEffect(function () {
        setInternalTheme(themeProp);
    }, [themeProp]);
    // Context
    var _e = useHawkinsContext(), contextDefaultClasses = _e.defaultClasses, contextDefaultProps = _e.defaultProps, contextTheme = _e.theme, contextHideDeprecationWarnings = _e.hideDeprecationWarnings;
    // Default to the parent context's theme, but allow the user to override it.
    var theme = internalTheme || contextTheme || 'light';
    var hasParentProvider = !!contextTheme;
    var providerValue = React.useMemo(function () { return ({
        // Lodash `mergeWith()` mutates its first argument, so start with a fresh object.
        defaultClasses: mergeWith({}, contextDefaultClasses, defaultClasses, classesMergeFn),
        defaultProps: mergeWith({}, contextDefaultProps, defaultProps, propsMergeFn),
        setTheme: setInternalTheme,
        theme: theme,
        loading: loading,
        compact: compact,
        hideDeprecationWarnings: contextHideDeprecationWarnings || hideDeprecationWarnings,
    }); }, [
        compact,
        contextDefaultClasses,
        contextDefaultProps,
        defaultClasses,
        defaultProps,
        loading,
        theme,
        hideDeprecationWarnings,
        contextHideDeprecationWarnings,
    ]);
    return (_jsx(HawkinsContext.Provider, __assign({ value: providerValue }, { children: _jsxs(HawkinsJSSProvider, __assign({ externalTheme: externalTheme, generateClassName: generateClassName, insertionPoint: insertionPoint, theme: theme }, { children: [!hasParentProvider && includeCssReset && _jsx(CSSReset, {}), !hasParentProvider && includeCssBaseline && _jsx(CSSBaseline, {}), children] })) })));
};
HawkinsProvider.displayName = 'HawkinsProvider';
